import { FC, useId, useRef } from "react"

import { ReactComponent as IconClose } from "@common_assets/svg/cross.svg"
import { ReactComponent as IconImage } from "@common_assets/svg/image-outlined.svg"
import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material"
import QuantityStepper from "components/common/button/quantityStepper"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { IClassStoreItem } from "ts/interfaces/Class"

import { Price } from "../SendStoreChargeDialog.parts"
import { isItemOutOfStock } from "../SendStoreChargeDialog.utils"
import { TileButton } from "./StoreItemTile.styles"

interface IProps {
  item: IClassStoreItem
  quantity: number
  onAdd: () => void
  onDecrement: () => void
  onRemove: () => void
  // Set when a cart cap blocks adding: id of the helper explaining why.
  blockedReasonId?: string
}

const StoreItemTile: FC<IProps> = ({
  item,
  quantity,
  onAdd,
  onDecrement,
  onRemove,
  blockedReasonId,
}) => {
  const { t } = useTranslation()
  const id = useId()
  const priceId = `${id}-price`
  const stockId = `${id}-stock`
  const inCartId = `${id}-in-cart`

  const tileRef = useRef<HTMLButtonElement>(null)

  const outOfStock = isItemOutOfStock(item)
  const selected = quantity > 0
  const blocked = outOfStock || !!blockedReasonId
  // Line cap only hits unselected tiles; the x500 cap keeps the selected look.
  const lineCapped = !!blockedReasonId && !selected

  const stockLabel = outOfStock
    ? t("store.outOfStock")
    : item.quantity_in_stock == null
      ? t("storeCharge.stockUnlimited")
      : t("storeCharge.tile.stockLeft", { count: item.quantity_in_stock })

  return (
    <Box position="relative" height="100%">
      <TileButton
        ref={tileRef}
        type="button"
        aria-label={t("storeCharge.tile.addA11y", { item: item.name })}
        aria-describedby={[
          priceId,
          stockId,
          selected ? inCartId : null,
          blockedReasonId,
        ]
          .filter(Boolean)
          .join(" ")}
        aria-disabled={blocked || undefined}
        onClick={() => {
          if (!blocked) onAdd()
        }}
        sx={(theme) => ({
          width: "100%",
          height: "100%",
          p: "16px",
          // Room for the quantity stepper row.
          pb: selected ? "52px" : "16px",
          display: "flex",
          gap: "12px",
          alignItems: "flex-start",
          textAlign: "left",
          font: "inherit",
          cursor: outOfStock || lineCapped ? "not-allowed" : "pointer",
          ...(outOfStock
            ? { opacity: 0.5 }
            : lineCapped
              ? { opacity: 0.7 }
              : {
                  "&:hover": {
                    background: theme.palette.mockup.primary95,
                    borderColor: theme.palette.mockup.primary95,
                  },
                }),
          ...(selected && {
            background: theme.palette.primary.light,
            borderColor: theme.palette.primary.main,
            "&:hover": {
              background: theme.palette.primary.light,
              borderColor: theme.palette.primary.main,
            },
          }),
        })}
      >
        <Avatar
          src={item.icon_url ?? undefined}
          alt=""
          variant="rounded"
          sx={{
            width: "40px",
            height: "40px",
            flexShrink: 0,
            borderRadius: "8px",
            bgcolor: "mockup.primary95",
            color: "mockup.primary10",
            "& svg": { width: "20px", height: "20px" },
          }}
        >
          <IconImage />
        </Avatar>
        <Stack gap="4px" minWidth={0} flex={1} pr={selected ? "28px" : 0}>
          <Typography
            variant="body2"
            fontWeight={500}
            color="mockup.neutral10"
            sx={{ overflowWrap: "anywhere" }}
          >
            {item.name}
          </Typography>
          <Typography
            id={stockId}
            variant="body3"
            // neutral20 on the selected primary90 tint is 3.98:1 in Bankee — below AA.
            color={selected ? "mockup.neutral10" : "mockup.neutral20"}
          >
            {stockLabel}
          </Typography>
          <Box id={priceId}>
            <Price amount={item.price} />
          </Box>
          {selected && (
            <Box component="span" id={inCartId} sx={visuallyHiddenSx}>
              {t("storeCharge.tile.inCartA11y", { count: quantity })}
            </Box>
          )}
        </Stack>
      </TileButton>
      {selected && (
        <Box position="absolute" right="12px" bottom="12px">
          <QuantityStepper
            quantityText={t("storeCharge.tile.quantity", { count: quantity })}
            decreaseLabel={t("storeCharge.tile.decreaseA11y", {
              item: item.name,
            })}
            increaseLabel={t("storeCharge.tile.increaseA11y", {
              item: item.name,
            })}
            increaseBlockedReasonId={blockedReasonId}
            onDecrease={() => {
              // The stepper unmounts when the last unit goes.
              if (quantity === 1) tileRef.current?.focus()
              onDecrement()
            }}
            onIncrease={onAdd}
          />
        </Box>
      )}
      {selected && (
        <IconButton
          size="small"
          aria-label={t("storeCharge.tile.removeA11y", { item: item.name })}
          onClick={(event) => {
            event.stopPropagation()
            onRemove()
            tileRef.current?.focus()
          }}
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            width: "28px",
            height: "28px",
          }}
        >
          {/* cross.svg is 24×24; a smaller box let it overflow and sit off-centre on the hover circle. */}
          <IconWrapper aria-hidden sx={{ "& svg": { display: "block" } }}>
            <IconClose />
          </IconWrapper>
        </IconButton>
      )}
    </Box>
  )
}

export default StoreItemTile
